import i18n from 'i18next';
import { actions, assign, createMachine, sendParent } from 'xstate';
import { NavigationSections } from '../constants';
import { QueryParamKeys } from '../constants/query-params';
import {
  ApplyTypes,
  AppStatuses,
  AppSteps,
  BankAccountInput,
  ChannelTypes,
  CustomerSearchInput,
  ProtectionPlusStatus,
  SoftDeclineTypes,
  UpdateBankAccountErrors,
} from '../gql/graphql';
import apply from '../networkRequests/apply/apply';
import createDraftApp, { CreateDraftAppResult } from '../networkRequests/apply/createDraftApp';
import { plaidUpdateBankAccount, updateBankAccount } from '../networkRequests/bankAccount/updateBankAccount';
import getAdditionalCustomerInfo from '../networkRequests/basicInfo/getAdditionalCustomerInfo';
import updateBasicInfoContinued from '../networkRequests/basicInfo/updateBasicInfoContinued';
import verifyEmail from '../networkRequests/basicInfo/verifyEmail';
import authorizeCard from '../networkRequests/debitCard/authorizeCard';
import updateDebitInfo from '../networkRequests/debitCard/updateDebitCard';
import updateEmployment from '../networkRequests/employment/updateEmployment';
import { PaymentSelectionErrors } from '../pages/paymentSelectionError/PaymentSelectionError';
import getApplicationId from '../utils/commonState/getApplicationId';
import { PropagateNetworkErrors, sharedActions } from './actions/genericActions';
import { identify, page, track } from './actions/segment';
import { trackContractSummaryAcknowledgeFailed } from './actions/trackContractSummaryAcknowledgeFailed';
import { trackEmailValidationError } from './actions/trackEmailValidationError';
import { AssignApplyResult, AssignCustomerSearchResult } from './actions/sharedNetworkActions';
import { AssignGeneratedLeaseDocuments, AssignInitialLeaseDocumentLanguage, createLanguageChangedTransitions } from './actions/leaseDocumentLanguage';
import { MakeStateCallingQueryOrder, MakeStateCallingQueryOrderWithCartValidation } from './refresh-order';
import queryOrder from '../networkRequests/order/queryOrder';
import { BasicInfoContinuedModel, EcommMachineEvents } from './types/ecommMachine.types';
import {
  LongFormEvents,
  LongFormMachineContext,
  LongFormMachineEvents,
  LongFormMachineGuards,
  LongFormMachineStates,
  LongFormStates,
} from './types/longFormMachine.types';
import { CustomerFlowType } from '../constants/customer-flow-type';
import fetchLinkToken from '../networkRequests/plaid/getLinkToken';
import generateContract from '../networkRequests/lease/generateContract';
import signLeaseAgreement from '../networkRequests/lease/signLeaseAgreement';
import selectLeaseOption from '../networkRequests/lease/selectLeaseOption';
import acknowledgeLeaseOptions from '../networkRequests/lease/acknowledgeLeaseOptions';
import getProtectionPlusEnrollmentUrl from '../networkRequests/lease/getProtectionPlusEnrollmentUrl';
import { isValidAddress } from '../helpers/validators/address.helper';
import { queryCustomers } from '../networkRequests/customerSearch/queryCustomers';
import { ReviewAndSubmitAlertTypes } from '../pages/reviewAndSubmit/ReviewAndSubmitAlert';
import getApplicationItems from '../networkRequests/cart/getApplicationItems';
import getExistingLeaseDocuments from '../networkRequests/lease/getExistingLeaseDocuments';
import { redirectToApplicationDetails } from '../helpers/url/url.helpers';
import { merchantRoles } from '../types/role';
import { canAccessContractSummaryPage, canAccessMerchantContractSummaryPage } from '../helpers/contractSummaryAccess/contractSummaryAccess';
import { determinePlaidFF } from '../helpers/plaid/plaid.helper';
import { FLAG_DISABLE_CUSTOMER_DEVICE_CONTRACT_SIGNING, FLAG_NEW_C2_EXPERIENCE } from '../constants/feature-flags';

export const canBypassBDSPage = (context: LongFormMachineContext) => {
  const bdsFF = context.ldClient.variation(FLAG_DISABLE_CUSTOMER_DEVICE_CONTRACT_SIGNING, false);
  return !bdsFF || context.contractSummaryPageParam;
};

export default createMachine<LongFormMachineContext, LongFormMachineEvents, LongFormMachineStates>(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5gF8A0IB2B7CdGgBEAnAQwDMAXfEABy1gEsKGsNqAPRARgCZ0BPbj2QjkQA */
    id: 'LongForm',
    initial: LongFormStates.Entry,
    context: {} as LongFormMachineContext, // Nothing you put here matters, the init block is the data in the `src: longFormApplicationMachine` line in ecomm-machine
    on: {
      [LongFormEvents.TRACK]: {
        actions: (context, event: any) => track(event?.data?.eventName, event?.data?.payload)(context, event),
      },
      [LongFormEvents.PAGE]: {
        actions: (context, event: any) => page(event?.data?.pageName, event?.data?.payload)(context, event),
      },
      [LongFormEvents.IDENTIFY]: {
        actions: (context, event: any) => identify(event?.data?.customerSorId, event?.data?.payload),
      },
    },
    states: {
      [LongFormStates.Entry]: {
        always: [
          // SKIP TO PAGE CONDITIONS TAKE PRECEDENCE
          {
            target: LongFormStates.ContractSummaryEcomm,
            cond: 'SkipToContractSummary',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.ApprovalConfirmation,
            cond: 'SkipToApprovalConfirmation',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.Declined,
            cond: 'SkipToDeclined',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.ReviewAndSubmit,
            cond: 'SkipToReviewAndSubmit',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.Approval,
            cond: (ctx) => ctx.skipToPage === 'Approval' && ctx.highestSupportedApplicationMilestone >= 4,
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.ContractSummaryBM,
            cond: (ctx) => ctx.skipToPage === 'ContractSummaryBM' && ctx.highestSupportedApplicationMilestone >= 4,
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.LeaseAgreement,
            cond: (ctx) => {
              return ctx.skipToPage === 'LeaseAgreement' && ctx.highestSupportedApplicationMilestone >= 4 && canBypassBDSPage(ctx);
            },
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.DeferContractSigning,
            cond: (ctx) => {
              return ctx.skipToPage === 'LeaseAgreement' && ctx.highestSupportedApplicationMilestone >= 4 && !canBypassBDSPage(ctx);
            },
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.LeaseComplete,
            cond: (ctx) => ctx.skipToPage === 'LeaseComplete' && ctx.highestSupportedApplicationMilestone >= 4,
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.CompleteAppLater,
            cond: 'SkipToCompleteAppLater',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.LeaseAgreement,
            cond: (ctx) => ctx.skipToPage === LongFormStates.LeaseAgreement,
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          // CHECK APP STATUS CONDITIONS NEXT
          {
            target: LongFormStates.ReviewAndSubmit,
            cond: 'ViewApplicationDetails',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.PreApproval,
            cond: 'PreApproval',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            cond: (ctx) => canAccessContractSummaryPage(ctx) && ctx.application?.lease?.status === AppStatuses.DocsReady,
            target: LongFormStates.LeaseAgreement,
          },
          {
            cond: (ctx) => ctx.application?.lease?.status === AppStatuses.DocsReady && ctx.highestSupportedApplicationMilestone > 3 && !canBypassBDSPage(ctx),
            target: LongFormStates.DeferContractSigning,
          },
          {
            cond: (ctx) => ctx.application?.lease?.status === AppStatuses.DocsReady && ctx.highestSupportedApplicationMilestone > 3,
            target: LongFormStates.LeaseAgreement,
          },
          {
            cond: (ctx) =>
              ctx.highestSupportedApplicationMilestone > 3 &&
              [AppStatuses.DocsSigned, AppStatuses.ApprovedForFunding, AppStatuses.Funded, AppStatuses.ReadyForFunding].includes(
                ctx.application?.lease?.status!,
              ),
            target: '#LongForm.LeaseAgreement.callGetExistingLeaseDocuments',
          },
          {
            cond: (ctx) =>
              (canAccessContractSummaryPage(ctx) || canAccessMerchantContractSummaryPage(ctx)) &&
              ctx.application?.lease?.status === AppStatuses.Approved &&
              !!ctx.applicationItems.length,
            target: LongFormStates.ContractSummaryBM,
          },
          { target: LongFormStates.Approval, cond: 'IsEcommPostIP' },
          { target: LongFormStates.LeaseComplete, cond: 'IsEcommLeaseComplete' },
          {
            target: LongFormStates.ApprovalConfirmation,
            // Pre-M4 stores should not pass the Approval Confirmation page
            cond: 'PostApprovalBM',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.PersonalInformation,
            cond: 'NoApplication',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          // APP STEP CONDITIONS ARE LOWEST PRECEDENCE
          {
            target: LongFormStates.PersonalInformation,
            cond: 'AppStepPersonalInformation',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.ContactInformation,
            cond: 'AppStepContactData',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.IncomeSource,
            cond: 'AppStepIncomeSource',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.PaymentSelection,
            cond: 'AppStepBankingInformationFF',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.BankingInformation,
            cond: 'AppStepBankingInformation',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.DebitCardInformation,
            cond: 'AppStepDebitCardInformation',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.ReviewAndSubmit,
            cond: 'AppStepReviewAndSubmit',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.ReviewAndSubmit,
            cond: 'AppStepAmendAndResubmit',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
          {
            target: LongFormStates.PersonalInformation,
            cond: 'NoAppStep',
            actions: assign({ transitionedFrom: LongFormStates.Entry }),
          },
        ],
      },
      [LongFormStates.PersonalInformation]: {
        initial: 'waiting',
        states: {
          waiting: {
            on: {
              [LongFormEvents.NEXT]: {
                target: 'callingVerifyEmail',
                actions: assign({
                  verifyEmailErrors: [],
                  basicInfo: (_, event) => event.data,
                }),
              },
            },
          },
          callingVerifyEmail: {
            tags: 'calling',
            invoke: {
              id: 'verifyEmail',
              src: (_, event) => verifyEmail(event.data?.emailAddress!),
              onDone: [
                {
                  cond: (_, event) => !!event.data?.length,
                  target: 'waiting',
                  actions: [assign({ verifyEmailErrors: (_, event) => event.data }), trackEmailValidationError],
                },
                {
                  target: '#LongForm.reviewAndSubmit',
                  cond: 'TransitionedFromReviewAndSubmit',
                  actions: (context) => [
                    assign({
                      transitionedFrom: LongFormStates.PersonalInformation,
                      // Temporary solution to clear review and submit errors when editing this page from review and submit.
                      // Can be removed if basic info page is updated to call AES when editing from review and submit
                      dismissibleAlerts: [...context.dismissibleAlerts, ReviewAndSubmitAlertTypes.Default, ReviewAndSubmitAlertTypes.NewC2Exp],
                    }),
                    identify(undefined, {
                      // dont pass user id that will be handled by cust search
                      name: context.basicInfo?.firstName + ' ' + context.basicInfo?.lastName, // for backwards compatibility
                      emailAddress: context.basicInfo?.emailAddress,
                    }),
                  ],
                },
                {
                  target: '#LongForm.contactInformation',
                  actions: (context) => [
                    assign({ transitionedFrom: LongFormStates.PersonalInformation }),
                    identify(undefined, {
                      // dont pass user id that will be handled by cust search
                      name: context.basicInfo?.firstName + ' ' + context.basicInfo?.lastName,
                      emailAddress: context.basicInfo?.emailAddress,
                    }),
                  ],
                },
              ],
              onError: { actions: PropagateNetworkErrors() },
            },
          },
        },
        on: {
          [LongFormEvents.BACK]: [
            {
              target: LongFormStates.ReviewAndSubmit,
              cond: 'TransitionedFromReviewAndSubmit',
              actions: assign({ transitionedFrom: LongFormStates.ContactInformation }),
            },
          ],
          [LongFormEvents.CancelButtonClicked]: [
            {
              target: LongFormStates.ReviewAndSubmit,
              actions: assign({ transitionedFrom: LongFormStates.PersonalInformation }),
            },
          ],
        },
        meta: {
          navigationSection: NavigationSections.CHECK_ELIGIBILITY,
        },
      },
      [LongFormStates.ContactInformation]: {
        initial: 'waiting',
        states: {
          waiting: {
            on: {
              [LongFormEvents.SUBMIT_CONTACT_INFORMATION]: [
                {
                  cond: 'ApplicationExists',
                  target: 'callingUpdate',
                  actions: assign({
                    basicInfoContinued: (_context, event: any) => event.data?.basicInfoContinued as BasicInfoContinuedModel,
                    didCustomerChange: (_context, event: any) => !!event.data?.didCustomerChange,
                  }),
                },
                {
                  cond: (_, event) => !!event.data?.didCustomerChange,
                  target: 'callingSearch',
                  actions: assign({
                    basicInfoContinued: (_context, event: any) => event.data?.basicInfoContinued as BasicInfoContinuedModel,
                    didCustomerChange: (_context, event: any) => !!event.data?.didCustomerChange,
                  }),
                },
                {
                  cond: 'TransitionedFromReviewAndSubmit',
                  target: '#LongForm.reviewAndSubmit',
                  actions: assign({
                    basicInfoContinued: (_context, event: any) => event.data?.basicInfoContinued as BasicInfoContinuedModel,
                    didCustomerChange: (_context, event: any) => !!event.data?.didCustomerChange,
                    transitionedFrom: LongFormStates.ContactInformation,
                  }),
                },
                {
                  cond: 'TransitionedFromAppSteps',
                  target: '#LongForm.accommodationAddress',
                  actions: assign({
                    basicInfoContinued: (_context, event: any) => event.data?.basicInfoContinued as BasicInfoContinuedModel,
                    didCustomerChange: (_context, event: any) => !!event.data?.didCustomerChange,
                    transitionedFrom: LongFormStates.ContactInformation,
                  }),
                },
              ],
            },
          },
          callingUpdate: {
            tags: 'calling',
            invoke: {
              src: updateBasicInfoContinued,
              onDone: [
                {
                  // TODO: this needs to be accompanied by some sort of error text on the page
                  cond: (_context, event) => !!event.data?.updateBasicInfoContinuedErrors?.length,
                  target: 'waiting',
                },
                {
                  cond: (context) => context.didCustomerChange,
                  target: 'callingSearch',
                },
                // If we don't need to run/re-run customer search, pass to next page like normal
                {
                  cond: 'TransitionedFromReviewAndSubmit',
                  target: '#LongForm.reviewAndSubmit',
                  actions: assign({ transitionedFrom: LongFormStates.ContactInformation }),
                },
                {
                  target: '#LongForm.accommodationAddress',
                  cond: 'TransitionedFromAppSteps',
                  actions: assign({ transitionedFrom: LongFormStates.ContactInformation }),
                },
              ],
              onError: { actions: PropagateNetworkErrors() },
            },
          },
          callingSearch: {
            tags: 'calling',
            invoke: {
              // This is defined inline because the previous network call overwrites the button click event data that has taxId and birthDate
              // and the queryCustomersByIDV function does not account for that
              // TODO: refactor the queryCustomersByIDV function to take regular arguments, not a context and an event so we can call that here
              src: (context) => {
                const customerCheckPayload: CustomerSearchInput = {
                  orderId: context.orderId,
                  cellPhone: context.cellPhone,
                  taxId: context.basicInfoContinued?.taxpayerIdentificationNumber,
                  birthDate: context.basicInfoContinued?.birthDate,
                };

                return queryCustomers(context, customerCheckPayload);
              },
              onDone: [
                {
                  target: '#LongForm.ineligible',
                  cond: 'IsIneligible',
                  actions: [track('SSN DOB ROE response', { response: 'returning - ineligible' }), AssignCustomerSearchResult],
                },
                {
                  // This guard catches the `customer is ineligible` resolver error.
                  target: '#LongForm.ineligible',
                  cond: 'IsIneligibleResolverError',
                  actions: [track('SSN DOB ROE response', { response: 'returning - ineligible' }), AssignCustomerSearchResult],
                },
                /**
                 * For OTB eligible customers, do not allow updates to phone number within the application flow to prevent fraud.
                 * This is a temporary measure until email verification can be implemented
                 * The cellphone changing is a major fraud indicator, which presents as a different phone number, or a skipped OTP
                 */
                {
                  target: '#LongForm.PhoneNumberChanged',
                  cond: (context, event) =>
                    event?.data?.eligibilityDetails?.applyTypes[0] === ApplyTypes.LeaseOtb &&
                    (context.cellPhone !== event?.data?.customers[0]?.cellPhone || context.didSkipOTP),
                  actions: [
                    (context, event) => track('Contact Info Mismatch', { customerSorId: event?.data?.customers[0]?.id })(context, event),
                    AssignCustomerSearchResult,
                  ],
                },
                /**
                 * If we find that the customer is OTB eligible after collecting date of birth and full SSN, the OTB machine will be invoked.
                 * This means that they will immediately see the SSN last four verification screen.  This was a conscious desision to satisfy
                 * compliance requirements around providing appropriate disclosures before application submission.  This isn't really the cleanest
                 * experience, so consider revisiting this approach in the future.
                 */
                {
                  cond: 'CustomerOTB',
                  target: '#LongForm.otb',
                  actions: [track('SSN DOB ROE response', { response: 'returning - OTB eligible' }), AssignCustomerSearchResult],
                },
                // if they came from R&S, send them back to R&S
                // slight bug here that this ignores their eligibility checks, but those will be rechecked by the eligibility checks
                // TODO: since we're ignoring the customerSearch result, we should just skip the call.
                // The condition for that is complicated, ask Tom for help if unclear
                {
                  cond: 'TransitionedFromReviewAndSubmit',
                  target: '#LongForm.reviewAndSubmit',
                  actions: assign({ transitionedFrom: LongFormStates.ContactInformation }), // do not assign since we're goign back to R&S
                },
                {
                  cond: 'CustomerNotFound',
                  target: '#LongForm.accommodationAddress',
                  actions: [
                    track('SSN DOB ROE response', { response: 'new customer' }),
                    assign({ transitionedFrom: LongFormStates.ContactInformation }),
                    AssignCustomerSearchResult,
                  ],
                },
                {
                  cond: 'TransitionedFromAppSteps',
                  target: '#LongForm.accommodationAddress',
                  actions: [
                    track('SSN DOB ROE response', { response: 'returning - new app' }),
                    assign({ transitionedFrom: LongFormStates.ContactInformation }),
                    AssignCustomerSearchResult,
                  ],
                },
              ],
              onError: { actions: PropagateNetworkErrors() },
            },
          },
        },
        on: {
          [LongFormEvents.BACK]: [
            {
              target: '#LongForm.personalInformation',
              cond: 'TransitionedFromAppSteps',
              actions: assign({ transitionedFrom: LongFormStates.ContactInformation }),
            },
            {
              target: '#LongForm.reviewAndSubmit',
              cond: 'TransitionedFromReviewAndSubmit',
              actions: assign({ transitionedFrom: LongFormStates.ContactInformation }),
            },
          ],
          [LongFormEvents.CancelButtonClicked]: [
            {
              target: '#LongForm.reviewAndSubmit',
              actions: assign({ transitionedFrom: LongFormStates.ContactInformation }),
            },
          ],
        },
        meta: {
          navigationSection: NavigationSections.CHECK_ELIGIBILITY,
          canGoBackViaHeader: true,
        },
      },
      // comes after basic info continue, but doesnt have an associated appStep
      [LongFormStates.AccommodationAddress]: {
        initial: 'waiting',
        states: {
          waiting: {
            on: {
              [LongFormEvents.NEXT]: [
                // Temporary solution to clear review and submit errors when editing this page from review and submit.
                // Can be removed if billing address page is updated to call AES when editing from review and submit
                {
                  // only create if there's no existing app, otherwise we will update the existing app via apply request body on review & submit
                  cond: (context, event) => !getApplicationId(context.application),
                  target: 'callingCreateDraftApp',
                  actions: ['assignAccommodationAndBillingAddresses', assign({ createDraftAppErrors: [] })],
                },
                {
                  target: '#LongForm.incomeSource',
                  cond: 'TransitionedFromAppSteps',
                  actions: ['assignAccommodationAndBillingAddresses', assign({ transitionedFrom: LongFormStates.AccommodationAddress })],
                },
                {
                  target: '#LongForm.reviewAndSubmit',
                  cond: 'TransitionedFromReviewAndSubmit',
                  actions: [
                    'assignAccommodationAndBillingAddresses',
                    assign({
                      transitionedFrom: LongFormStates.AccommodationAddress,
                      // Temporary solution to clear review and submit errors when editing this page from review and submit.
                      // Can be removed if billing address page is updated to call AES when editing from review and submit
                      dismissibleAlerts: (context) => [...context.dismissibleAlerts, ReviewAndSubmitAlertTypes.Default, ReviewAndSubmitAlertTypes.NewC2Exp],
                    }),
                  ],
                },
              ],
            },
          },
          callingCreateDraftApp: {
            tags: 'calling',
            invoke: {
              src: createDraftApp,
              onDone: [
                {
                  cond: 'LTOIneligibleAutoVertical',
                  target: '#LongForm.LTOIneligible',
                  actions: [
                    track('Non Leaseable Vertical in State', { errorType: 'Ineligible' }),
                    assign({
                      autoVerticalError: true,
                    }),
                  ],
                },
                {
                  cond: 'LTOIneligible',
                  target: '#LongForm.LTOIneligible',
                  actions: [track('Non Leaseable State', { errorType: 'Ineligible' })],
                },
                {
                  cond: (_context, event) => {
                    const data = event.data as CreateDraftAppResult;
                    return !!data.application?.lease?.id;
                  },
                  target: 'callingQueryOrder',
                  actions: [
                    assign({
                      application: (_ctx, event) => event.data.application, // this will get overwritten by queryOrder, very important that it does because this only has app id on it
                      customerFlowType: (_ctx, event) => event.data.customerFlowType,
                    }),
                  ],
                },
                {
                  cond: (_context, event) => {
                    const data = event.data as CreateDraftAppResult;
                    return !!data.createDraftAppErrors?.length;
                  },
                  target: 'waiting',
                  actions: [assign({ createDraftAppErrors: (_ctx, event) => event.data.createDraftAppErrors })],
                },
              ],
              onError: { actions: PropagateNetworkErrors() },
            },
          },
          callingQueryOrder: MakeStateCallingQueryOrder('done'),
          // need a transition state to use the query order factory
          done: {
            always: [
              {
                target: '#LongForm.reviewAndSubmit',
                cond: 'TransitionedFromReviewAndSubmit',
                actions: assign({ transitionedFrom: LongFormStates.AccommodationAddress }),
              },
              {
                target: '#LongForm.incomeSource',
                actions: assign({ transitionedFrom: LongFormStates.AccommodationAddress }),
              },
            ],
          },
        },
        on: {
          [LongFormEvents.BACK]: [
            {
              target: LongFormStates.ContactInformation,
              cond: 'TransitionedFromAppSteps',
              actions: assign({ transitionedFrom: LongFormStates.ContactInformation }),
            },
            {
              target: LongFormStates.ReviewAndSubmit,
              cond: 'TransitionedFromReviewAndSubmit',
              actions: assign({ transitionedFrom: LongFormStates.ContactInformation }),
            },
          ],
          [LongFormEvents.CancelButtonClicked]: [
            {
              target: LongFormStates.ReviewAndSubmit,
              actions: assign({ transitionedFrom: LongFormStates.AccommodationAddress }),
            },
          ],
          [LongFormEvents.VIEW_PREQUALIFICATION_NOTICE]: {
            actions: 'assignAccommodationAndBillingAddresses',
            target: LongFormStates.PrequalificationNotice,
          },
        },
        meta: {
          canGoBackViaHeader: true,
        },
      },
      [LongFormStates.LTOIneligible]: {},
      [LongFormStates.IncomeSource]: {
        initial: 'waiting',
        states: {
          waiting: {
            on: {
              [LongFormEvents.NEXT]: [
                {
                  target: LongFormStates.UpdateEmployment,
                  actions: [actions.assign({ payFrequencyDetails: (_context, event) => event.data! })],
                },
              ],
            },
          },
          [LongFormStates.UpdateEmployment]: {
            tags: 'calling',
            invoke: {
              src: (context: LongFormMachineContext, event: LongFormMachineEvents) => updateEmployment(context.application, event.data!),
              onDone: [
                {
                  cond: (_, event) => !!event?.data?.updateEmploymentErrors?.length,
                  target: 'waiting',
                  actions: [assign({ updateEmploymentErrors: (_, event) => event?.data?.updateEmploymentErrors }), track('Payment Schedule Update Error')],
                },
                {
                  cond: 'TransitionedFromReviewAndSubmit',
                  target: '#LongForm.reviewAndSubmit',
                  actions: assign({ transitionedFrom: LongFormStates.IncomeSource }),
                },
                // Feature flag takes precedence over other checks.
                {
                  cond: 'PaymentSelectFF',
                  target: '#LongForm.PaymentSelection',
                  actions: assign({ transitionedFrom: LongFormStates.IncomeSource }),
                },
                {
                  cond: 'TransitionedFromAppSteps',
                  target: '#LongForm.bankingInformation',
                  actions: assign({ transitionedFrom: LongFormStates.IncomeSource }),
                },
              ],
              onError: { actions: PropagateNetworkErrors() },
            },
          },
        },
        on: {
          [LongFormEvents.BACK]: [
            {
              target: '#LongForm.accommodationAddress',
              cond: 'TransitionedFromAppSteps',
              actions: assign({ transitionedFrom: LongFormStates.IncomeSource }),
            },
            {
              target: '#LongForm.reviewAndSubmit',
              cond: 'TransitionedFromReviewAndSubmit',
              actions: assign({ transitionedFrom: LongFormStates.ContactInformation }),
            },
          ],
          [LongFormEvents.CancelButtonClicked]: [
            {
              target: '#LongForm.reviewAndSubmit',
              actions: assign({ transitionedFrom: LongFormStates.ContactInformation }),
            },
          ],
        },
        meta: {
          navigationSection: NavigationSections.PAYMENT_DETAILS,
          canGoBackViaHeader: true,
        },
      },
      [LongFormStates.PaymentSelection]: {
        initial: 'PaymentSelectionInit',
        states: {
          PaymentSelectionInit: {
            on: {
              [LongFormEvents.SELECT_PLAID]: [
                {
                  target: LongFormStates.FetchLinkToken,
                },
              ],
              [LongFormEvents.SELECT_MANUAL]: {
                target: '#LongForm.bankingInformation',
              },
              [LongFormEvents.BACK]: [
                {
                  target: '#LongForm.reviewAndSubmit',
                  cond: 'TransitionedFromReviewAndSubmit',
                  actions: assign({ transitionedFrom: LongFormStates.BankingInformation }),
                },
                {
                  target: '#LongForm.incomeSource',
                },
              ],
            },
          },
          [LongFormEvents.FETCH_LINK_TOKEN]: {
            tags: 'calling',
            invoke: {
              id: 'FetchLinkToken',
              src: (context) => fetchLinkToken(context.orderId),
              onDone: [
                {
                  cond: 'LinkTokenFailure',
                  target: '#LongForm.PaymentSelectionError',
                  actions: assign({
                    // currently we don't actually get any error data back from the bankConnection if link token fails, so we're using the default Plaid error. Should be fixed.
                    bankConnection: (context) => ({ ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError }),
                  }),
                },
                {
                  target: '#LongForm.PlaidModal',
                  actions: assign({
                    plaidToken: (_context, event: any) => {
                      return {
                        linkToken: event?.data?.linkToken,
                        publicToken: '',
                        bankAcctLast4: '',
                      };
                    },
                  }),
                },
              ],
              onError: {
                target: '#LongForm.PaymentSelectionError',
                actions: assign({
                  bankConnection: (context, event) => ({ ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError }),
                }),
              },
            },
          },
        },
        meta: {
          navigationSection: NavigationSections.PAYMENT_DETAILS,
          canGoBackViaHeader: true,
        },
      },
      [LongFormStates.PlaidModal]: {
        initial: 'PlaidModalInit',
        states: {
          PlaidModalInit: {
            on: {
              [LongFormEvents.BACK]: {
                target: '#LongForm.PaymentSelection',
              },
              [LongFormEvents.ERROR]: {
                target: '#LongForm.PaymentSelectionError',
                actions: [
                  actions.assign({
                    bankConnection: (context, event) => {
                      return { ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError };
                    },
                  }),
                ],
              },
              [LongFormEvents.NEXT]: [
                {
                  target: 'PlaidUpdateBankAccount',
                  actions: [
                    actions.assign({
                      bankDetails: (context: LongFormMachineContext, event) =>
                        ({ ...context.bankDetails, bankName: event?.data?.bankName } as BankAccountInput),
                      bankConnection: (context: LongFormMachineContext, event) => ({ ...context.bankConnection, bankAcctLast4: event!.data!.bankAcctLast4 }),
                      plaidToken: (context: LongFormMachineContext, event) => ({ ...context.plaidToken, publicToken: event?.data?.publicToken || '' }),
                    }),
                  ],
                },
              ],
            },
          },
          PlaidUpdateBankAccount: {
            tags: 'calling',
            invoke: {
              src: (context: LongFormMachineContext, event: LongFormMachineEvents) =>
                plaidUpdateBankAccount(getApplicationId(context.application), context.plaidToken.publicToken, context.plaidToken.linkToken),
              onDone: [
                {
                  cond: 'TokenizedAcct',
                  target: '#LongForm.bankingInformation',
                  actions: assign({
                    bankConnection: (context, event) => ({
                      ...context.bankConnection,
                      paymentSelectionError: PaymentSelectionErrors.plaidError,
                      isTokenizedAcct: true,
                    }),
                    plaidToken: (context, _event) => ({
                      ...context.plaidToken,
                      bankAcctLast4: '',
                    }),
                    bankDetails: (context, _event) => ({
                      ...context.bankDetails!,
                      bankName: '',
                    }),
                  }),
                },
                {
                  cond: 'BankError',
                  target: '#LongForm.PaymentSelectionError',
                  actions: assign({
                    bankConnection: (context) => {
                      return { ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError };
                    },
                  }),
                },
                {
                  cond: 'BankAccountHardDecline',
                  target: '#LongForm.declined',
                },
                {
                  cond: 'IneligibleBank',
                  target: '#LongForm.PaymentSelectionError',
                  actions: assign({
                    bankConnection: (context) => ({
                      ...context.bankConnection,
                      paymentSelectionError: PaymentSelectionErrors.ineligibleError,
                    }),
                  }),
                },
                {
                  // need to query order to grab the routing number and acct last 4
                  // because those aren't available in the plaid responsese
                  cond: 'PlaidSuccess',
                  target: 'callingQueryOrder',
                },
              ],
              onError: {
                target: '#LongForm.PaymentSelectionError',
                actions: assign({
                  bankConnection: (context) => ({
                    ...context.bankConnection,
                    // currently we don't actually get any error data back from the bankConnection if plaid bank update fully fails, so we're using the default Plaid error. Should be fixed.
                    paymentSelectionError: PaymentSelectionErrors.plaidError,
                  }),
                }),
              },
            },
          },
          callingQueryOrder: MakeStateCallingQueryOrder('#LongForm.PaymentReview'),
        },
      },
      [LongFormStates.PaymentSelectionError]: {
        on: {
          [LongFormEvents.SELECT_PLAID]: [{ target: LongFormStates.PaymentSelection }],
          [LongFormEvents.SELECT_MANUAL]: { target: LongFormStates.BankingInformation },
        },
      },
      [LongFormStates.PaymentReview]: {
        on: {
          [LongFormEvents.NEXT]: [
            {
              target: LongFormStates.ReviewAndSubmit,
              cond: 'TransitionedFromReviewAndSubmit',
              actions: assign({ transitionedFrom: LongFormStates.PaymentReview }),
            },
            {
              target: LongFormStates.DebitCardInformation,
            },
          ],
        },
      },
      [LongFormStates.BankingInformation]: {
        initial: 'BankingInformationInit',
        states: {
          BankingInformationInit: {
            on: {
              [LongFormEvents.NEXT]: [
                {
                  target: 'UpdateBankAccount',
                  actions: [
                    actions.assign({
                      bankDetails: (context, event) => {
                        return {
                          accountNumber: event?.data?.accountNumber || '',
                          routingNumber: event?.data?.routingNumber || '',
                          bankName: event?.data?.bankName || '',
                        };
                      },
                    }),
                  ],
                },
              ],
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
              canGoBackViaHeader: true,
            },
          },
          UpdateBankAccount: {
            tags: 'calling',
            invoke: {
              id: 'updateBankAccount',
              src: (context, event) =>
                updateBankAccount(getApplicationId(context.application), context.bankDetails!.accountNumber, context.bankDetails!.routingNumber),
              onDone: [
                // don't check errors, these will be handled at apply
                {
                  cond: 'TransitionedFromReviewAndSubmit',
                  target: '#LongForm.reviewAndSubmit',
                  actions: assign({
                    transitionedFrom: LongFormStates.BankingInformation,
                    dismissibleAlerts: (context) => [...context.dismissibleAlerts, ReviewAndSubmitAlertTypes.BankAccount],
                  }),
                },
                {
                  cond: 'TransitionedFromAppSteps',
                  target: '#LongForm.debitCardInformation',
                  actions: assign({ transitionedFrom: LongFormStates.BankingInformation }),
                },
              ],
              onError: { actions: PropagateNetworkErrors() },
            },
          },
        },
        on: {
          [LongFormEvents.BACK]: [
            {
              target: LongFormStates.ReviewAndSubmit,
              cond: 'TransitionedFromReviewAndSubmit',
              actions: assign({ transitionedFrom: LongFormStates.BankingInformation }),
            },
            {
              target: LongFormStates.PaymentSelection,
              cond: 'PaymentSelectFF',
              actions: assign({ transitionedFrom: LongFormStates.BankingInformation }),
            },
            {
              target: LongFormStates.IncomeSource,
              cond: 'TransitionedFromAppSteps',
              actions: assign({ transitionedFrom: LongFormStates.BankingInformation }),
            },
          ],
          [LongFormEvents.CancelButtonClicked]: [
            {
              target: LongFormStates.ReviewAndSubmit,
              actions: assign({ transitionedFrom: LongFormStates.BankingInformation }),
            },
          ],
        },
      },
      [LongFormStates.DebitCardInformation]: {
        initial: LongFormStates.DebitCardEntryInit,
        states: {
          [LongFormStates.DebitCardEntryInit]: {
            on: {
              [LongFormEvents.NEXT]: {
                target: LongFormStates.UpdateDebitCard,
                actions: assign({
                  debitInfo: (_context, event) => event.data!.debitDetails!,
                  billingAddress: (_context, event) => event.data!.billingAddress,
                }),
              },
            },
          },
          [LongFormStates.UpdateDebitCard]: {
            tags: 'calling',
            invoke: {
              src: (context: LongFormMachineContext, event: LongFormMachineEvents) =>
                updateDebitInfo(getApplicationId(context.application), context.debitInfo!, context.billingAddress!),
              onDone: [
                {
                  cond: (_, event) => !!event.data.updateDebitInfoErrors?.length,
                  target: LongFormStates.DebitCardEntryInit, // handle in place
                  actions: assign({ updateDebitInfoErrors: (_context, event) => event.data.updateDebitInfoErrors }),
                },
                {
                  target: '#LongForm.ContractSummaryEcomm',
                  cond: 'TransitionedFromContractSummaryEcomm',
                  actions: assign({ transitionedFrom: LongFormStates.DebitCardInformation }),
                },
                {
                  cond: 'TransitionedFromReviewAndSubmit',
                  actions: assign({
                    transitionedFrom: LongFormStates.DebitCardInformation,
                    // This clears errors on the R&S page when a customer is returning from fixing their debit card info
                    dismissibleAlerts: (context) => [...context.dismissibleAlerts, ReviewAndSubmitAlertTypes.DebitCard],
                  }),
                  target: '#LongForm.reviewAndSubmit',
                },
                // OR-2278: used to fall through to transferToRapid for a sub-M3 (or unset)
                // highestSupportedApplicationMilestone. That state is gone, and this field is
                // backend-controlled, so keep an unconditional fallback here rather than leave
                // this combination with no matching transition (this state is tagged 'calling',
                // which LongFormFlow renders as an unrecoverable loading spinner).
                {
                  target: '#LongForm.reviewAndSubmit',
                },
              ],
              onError: { actions: PropagateNetworkErrors() },
            },
          },
        },
        on: {
          [LongFormEvents.BACK]: [
            {
              target: LongFormStates.BankingInformation,
              cond: 'TransitionedFromAppSteps',
              actions: assign({ transitionedFrom: LongFormStates.DebitCardInformation }),
            },
            {
              target: LongFormStates.ReviewAndSubmit,
              cond: 'TransitionedFromReviewAndSubmit',
              actions: assign({ transitionedFrom: LongFormStates.DebitCardInformation }),
            },
            {
              target: LongFormStates.ContractSummaryEcomm,
              cond: 'TransitionedFromContractSummaryEcomm',
              actions: assign({
                transitionedFrom: LongFormStates.DebitCardInformation,
              }),
            },
          ],
          [LongFormEvents.CancelButtonClicked]: [
            {
              target: LongFormStates.ReviewAndSubmit,
              actions: assign({ transitionedFrom: LongFormStates.DebitCardInformation }),
            },
          ],
        },
        meta: {
          navigationSection: NavigationSections.PAYMENT_DETAILS,
          canGoBackViaHeader: true,
        },
      },
      [LongFormStates.ReviewAndSubmit]: {
        initial: 'callingGetAdditionalCustomerInfo',
        states: {
          callingGetAdditionalCustomerInfo: {
            invoke: {
              src: (context) => getAdditionalCustomerInfo(getApplicationId(context.application)),
              onDone: [
                {
                  // cond: (_context, event: any) => !!event.data?.fullTaxId,
                  target: 'waiting',
                  actions: assign({ additionalCustomerInfo: (_, event) => event.data }),
                },
                // note: there was no handler for graphql errors so I assume any failures would be only be network errors handled by PropagateNetworkErrors
              ],
              onError: { actions: PropagateNetworkErrors() },
            },
          },
          waiting: { on: { [LongFormEvents.NEXT]: 'callingApply' } },
          callingApply: {
            tags: 'calling',
            // once the form is re-submitted, we don't want to hide errors that were being hidden because the form was edited
            entry: assign({ dismissibleAlerts: [] }),
            invoke: {
              src: apply,
              // wait to handle apply result until after query order
              onDone: { target: 'callingQueryOrder', actions: AssignApplyResult },
              onError: { actions: PropagateNetworkErrors() },
            },
          },
          callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('select'), // needed for cart validation errors
          select: {
            always: [
              {
                cond: (context) => !!context.cartValidationError,
                target: '#LongForm.preApproval',
                actions: assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }),
              },
              {
                cond: 'IsHardDecline',
                target: '#LongForm.declined',
                actions: assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }),
              },
              {
                cond: 'IsSoftDecline',
                target: 'waiting',
                actions: [
                  assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }),
                  (context, event) =>
                    track('Application Error', {
                      errType: 'SoftDecline',
                      returningCustomer: parseInt(context?.application?.lease?.displayId?.split('-')?.[1]!) > 1,
                      decisionCode: context?.softDeclineError?.extensions?.decisionCode,
                      c2TestVersion:
                        context?.softDeclineError?.extensions?.decisionCode === 'C2'
                          ? context?.ldClient.variation(FLAG_NEW_C2_EXPERIENCE, false)
                            ? 'B'
                            : 'A' // A is control, B is new experience
                          : undefined,
                    })(context, event),
                ],
              },
              {
                cond: 'PreApproval',
                target: '#LongForm.preApproval',
                actions: assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }),
              },
              {
                cond: 'LTOIneligibleAutoVertical',
                target: '#LongForm.LTOIneligible',
                actions: [
                  assign({ transitionedFrom: LongFormStates.ReviewAndSubmit, autoVerticalError: true }),
                  track('Non Leaseable Vertical in State', { errorType: 'Ineligible' }),
                ],
              },
              {
                cond: 'LTOIneligible',
                target: '#LongForm.LTOIneligible',
                actions: [assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }), track('Non Leaseable State', { errorType: 'Ineligible' })],
              },
              {
                cond: 'ApplyIneligible',
                target: '#LongForm.ineligible',
                actions: [assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }), track('Non-Leaseable State', { errorType: 'Ineligible' })],
              },
              {
                target: '#LongForm.internalError',
                actions: assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }),
              },
            ],
          },
        },
        on: {
          [LongFormEvents.CONTACT_INFORMATION]: [
            {
              target: LongFormStates.ContactInformation,
              actions: assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }),
            },
          ],
          [LongFormEvents.PERSONAL_INFORMATION]: [
            {
              target: LongFormStates.PersonalInformation,
              actions: assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }),
            },
          ],
          [LongFormEvents.ACCOMMODATION_ADDRESS]: [
            {
              target: LongFormStates.AccommodationAddress,
              actions: assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }),
            },
          ],
          [LongFormEvents.INCOME_SOURCE]: [
            {
              target: LongFormStates.IncomeSource,
              actions: assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }),
            },
          ],
          [LongFormEvents.BANKING_INFORMATION]: [
            {
              target: LongFormStates.PaymentSelection,
              cond: (context, event) => determinePlaidFF(context),
              actions: assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }),
            },
            {
              target: LongFormStates.BankingInformation,
              actions: assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }),
            },
          ],
          [LongFormEvents.DEBIT_CARD_INFORMATION]: [
            {
              target: LongFormStates.DebitCardInformation,
              actions: assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }),
            },
          ],
          [LongFormEvents.BACK]: {
            target: LongFormStates.DebitCardInformation,
            // TODO: [M3] Handle how far you can go 'back' in the flow from ReviewAndSubmit. This will create a loop between debit card and review pages
            actions: assign({ transitionedFrom: LongFormStates.ReviewAndSubmit }),
          },
        },
        // meta: {
        //   navigationSection: NavigationSections.REVIEW_AND_PAY,
        //   canGoBackViaHeader: true,
        // },
      },
      [LongFormStates.ContractSummaryBM]: {
        initial: 'callingQueryOrder',
        entry: assign({ showNotAcknowledgedModal: false }),
        states: {
          // Just a precaution to ensure contract summaries exist
          callingQueryOrder: MakeStateCallingQueryOrder('callingGetAppItems'),
          callingGetAppItems: {
            tags: 'calling',
            invoke: {
              src: getApplicationItems,
              onDone: {
                target: 'waiting',
                actions: assign({
                  applicationItems: (_, event) => {
                    return event.data || [];
                  },
                }),
              },
              onError: { actions: PropagateNetworkErrors() },
            },
          },
          waiting: {
            on: {
              [LongFormEvents.NEXT]: {
                target: 'callingAcknowledgeLeaseOptions',
                actions: assign({
                  selectLeaseOptionErrors: [],
                  acknowledgeLeaseTermsErrors: [],
                  customerSelectedLeaseTerm: (_, event) => event.data as unknown as string,
                }),
              },
              [LongFormEvents.REFRESH_CONTRACT_SUMMARY_STATUS]: {
                target: 'refreshingOrderStatus',
                actions: assign({ showNotAcknowledgedModal: false }),
              },
              [LongFormEvents.CLOSE_NOT_ACKNOWLEDGED_MODAL]: {
                actions: assign({ showNotAcknowledgedModal: false }),
              },
            },
          },
          callingAcknowledgeLeaseOptions: {
            tags: 'calling',
            invoke: {
              src: acknowledgeLeaseOptions,
              onDone: [
                {
                  cond: (_, event) => event.data.selectLeaseOptionErrors?.length,
                  target: '#LongForm.internalError',
                },
                {
                  cond: (_, event) => event.data.acknowledgeLeaseTermsErrors?.length,
                  target: 'waiting',
                  actions: [
                    assign({
                      acknowledgeLeaseTermsErrors: (_context, event) => event.data.acknowledgeLeaseTermsErrors ?? [],
                    }),
                    trackContractSummaryAcknowledgeFailed,
                  ],
                },
                { target: 'callingSelectLeaseOption', actions: track('Contract Summary Acknowledge Successful') },
              ],
              onError: { actions: PropagateNetworkErrors() },
            },
          },
          refreshingOrderStatus: {
            tags: 'calling',
            invoke: {
              src: queryOrder,
              onDone: {
                target: 'checkRefreshResult',
                actions: assign({
                  application: (_, event) => event.data?.application,
                  orderDetails: (_, event) => event.data?.details,
                }),
              },
              onError: {
                target: 'waiting',
                actions: PropagateNetworkErrors(),
              },
            },
          },
          checkRefreshResult: {
            always: [
              {
                target: '#LongForm.LeaseAgreement',
                cond: (context) => context.application?.lease?.status === 'docsReady',
              },
              {
                actions: (context) => redirectToApplicationDetails(context.application?.lease?.publicId),
                cond: (context) => context.application?.lease?.status === 'docsSigned',
              },
              {
                target: 'waiting',
                actions: assign({ showNotAcknowledgedModal: true }),
              },
            ],
          },
          callingSelectLeaseOption: {
            tags: 'calling',
            invoke: {
              src: selectLeaseOption,
              onDone: [
                {
                  cond: (_, event) => event.data.selectLeaseOptionErrors?.length,
                  target: '#LongForm.internalError',
                },
                {
                  cond: (ctx) => ctx.ldClient.variation(FLAG_DISABLE_CUSTOMER_DEVICE_CONTRACT_SIGNING, false) && !ctx.contractSummaryPageParam,
                  target: '#LongForm.DeferContractSigning',
                },
                { target: '#LongForm.LeaseAgreement' },
              ],
              onError: { actions: PropagateNetworkErrors() },
            },
          },
        },
      },
      [LongFormStates.DeferContractSigning]: {
        initial: 'callingGenerateContract',
        states: {
          callingGenerateContract: {
            invoke: {
              src: (context, _) => generateContract(context.orderId),
              onDone: [
                {
                  cond: (_, event) => event.data?.generateContractErrors?.length,
                  actions: [
                    assign({ generateContractErrors: (_, event: any) => event.data?.generateContractErrors }),
                    track('Internal Error Occurred - Lease Agreement'),
                  ],
                },
                {
                  actions: assign({ leaseDocuments: (_, event: any) => event.data?.leaseDocuments }),
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
        },
      },
      // info pages
      [LongFormStates.PrequalificationNotice]: {
        on: {
          [LongFormEvents.NEXT]: {
            target: LongFormStates.AccommodationAddress,
          },
        },
      },
      [LongFormStates.Declined]: {},
      [LongFormStates.PreApproval]: {
        initial: 'waiting',
        states: {
          waiting: { on: { [LongFormEvents.NEXT]: { target: 'callingAuthorizeCard', actions: assign({ authorizeCardErrors: [] }) } } },
          callingAuthorizeCard: {
            tags: 'calling',
            invoke: {
              src: (context) => authorizeCard(getApplicationId(context.application)),
              onDone: [
                // TODO: generalize these so they can be reusable with the same apply guards
                {
                  cond: (_context, event) => !!event.data.hardDeclineError,
                  target: '#LongForm.declined',
                  actions: [
                    assign({
                      application: (_, event) => event?.data?.application,
                      hardDeclineError: (_, event) => event?.data?.hardDeclineError,
                      authorizeCardErrors: (_, event) => event.data.authorizeCardErrors,
                    }),
                    track('Debit Card Error', { errType: 'HardDecline' }),
                  ],
                },
                {
                  cond: (_context, event) => !!event.data.softDeclineError,
                  target: '#LongForm.reviewAndSubmit',
                  actions: [
                    assign({
                      application: (_, event) => event?.data?.application,
                      authorizeCardErrors: (_context, event) => event.data.authorizeCardErrors,
                    }),
                    /* TODO: Put this in when the track calls are removed from the page, otherwise they'll be duplicated
                    (context, event) => {
                      if (event.data.softDeclineError.extensions?.declineType === SoftDeclineTypes.PrepaidCardsNotAllowed) {
                        track('Debit Card Error', { errType: 'PrepaidCardError' })(context, event); // important to actually call track with (context, event);
                      } else if (event.data.softDeclineError.extensions?.declineType === SoftDeclineTypes.CreditCardsNotAllowed) {
                        track('Debit Card Error', { errType: 'CreditCardError' })(context, event); // important to actually call track with (context, event);
                      } else {
                        track('Debit Card Error', { errType: 'SoftDecline', decisionCode: event.data.softDeclineError?.extensions?.decisionCode })(
                          context,
                          event,
                        ); // important to actually call track with (context, event);
                      }
                    },
                    */
                  ],
                },
                {
                  cond: (_context, event) => !!event.data.authorizeCardErrors?.length,
                  target: 'waiting', // let the page handle it
                  actions: [
                    assign({
                      application: (_, event) => event?.data?.application,
                      authorizeCardErrors: (_context, event) => event.data.authorizeCardErrors,
                    }),
                    (context, event) => track('Application Error', { errorType: 'generalError', error: event.data.authorizeCardErrors[0] })(context, event),
                  ],
                },
                // ELF should skip the approval page becaue its unnecessary
                {
                  target: '#LongForm.ContractSummaryEcomm',
                  cond: (context) => context.dealer.channelType === ChannelTypes.Ecommerce,
                  actions: assign({
                    application: (_, event) => event?.data?.application,
                    transitionedFrom: LongFormStates.PreApproval,
                  }),
                },
                {
                  target: '#LongForm.approvalConfirmation',
                  actions: assign({
                    application: (_, event) => event?.data?.application,
                    transitionedFrom: LongFormStates.PreApproval,
                  }),
                },
              ],
              onError: { actions: PropagateNetworkErrors() },
            },
          },
        },

        on: {
          [LongFormEvents.COMPLETE_APP_LATER]: {
            target: LongFormStates.CompleteAppLater,
          },
        },
        meta: {
          canGoBackViaHeader: false,
        },
      },
      [LongFormStates.CompleteAppLater]: {
        on: {
          [LongFormEvents.BACK]: {
            target: LongFormStates.PreApproval,
          },
        },
      },
      [LongFormStates.Approval]: {
        // defensively, always make sure you have the most up to date order. This is for things like approval amount and cart validation
        initial: 'callingQueryOrder',
        states: {
          callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('waiting'),
          waiting: {
            on: {
              [LongFormEvents.NEXT]: [
                // for elf, approval is only reachable for resume/refresh flows. This acts like a landing page before showing the contract summary
                {
                  cond: (context) => context.dealer.channelType === ChannelTypes.Ecommerce,
                  target: '#LongForm.ContractSummaryEcomm',
                  actions: assign({ transitionedFrom: LongFormStates.Approval }),
                },
                {
                  target: '#LongForm.approvalConfirmation',
                  actions: assign({ transitionedFrom: LongFormStates.Approval }),
                },
              ],
            },
          },
        },
        meta: {
          canGoBackViaHeader: false,
        },
      },
      [LongFormStates.ApprovalConfirmation]: {
        entry: (context) => {
          if (context.highestSupportedApplicationMilestone > 3)
            console.log('long form context:', {
              role: context.role,
              applicationItems: context.applicationItems,
              queryParams: document.location.search,
              milestone: context.highestSupportedApplicationMilestone,
              appStatus: context.application?.lease?.status,
            });
        },
      },
      [LongFormStates.InternalError]: {},
      [LongFormStates.Ineligible]: {},
      [LongFormStates.PhoneNumberChanged]: {
        on: {
          [LongFormEvents.RESTART_APPLICATION]: {
            actions: [
              assign({ cellPhone: '', basicInfo: undefined, basicInfoContinued: undefined, accommodationAddress: undefined, billingAddress: undefined }),
              sendParent((context) => ({ type: EcommMachineEvents.CUSTOMER_LOOKUP_EVENT, data: { ...context } })),
            ],
          },
        },
      },
      [LongFormStates.Otb]: {
        type: 'final',
        entry: [
          sendParent((context, event) => ({
            data: {
              ...context,
            },
            type: EcommMachineEvents.OTB_CUSTOMER,
          })),
        ],
      },
      [LongFormStates.ContractSummaryEcomm]: {
        initial: 'callingQueryOrder',
        states: {
          // Just a precaution to ensure contract summaries exist
          callingQueryOrder: MakeStateCallingQueryOrder('waiting'),
          waiting: {
            on: {
              [LongFormEvents.NEXT]: {
                target: 'callingSelectLeaseOption',
                actions: assign({ selectLeaseOptionErrors: [] }),
              },
            },
          },
          callingSelectLeaseOption: {
            tags: 'calling',
            invoke: {
              src: selectLeaseOption,
              onDone: [
                {
                  cond: (_, event) => event.data.selectLeaseOptionErrors?.length,
                  target: '#LongForm.internalError',
                },
                { target: '#LongForm.LeaseAgreement' },
              ],
              onError: { actions: PropagateNetworkErrors() },
            },
          },
        },
        on: {
          [LongFormEvents.EDIT_DEBIT_INFO]: {
            target: LongFormStates.DebitCardInformation,
            actions: assign({
              transitionedFrom: LongFormStates.ContractSummaryEcomm,
            }),
          },
          [LongFormEvents.BACK]: {
            target: LongFormStates.Approval,
          },
        },
        meta: {
          navigationSection: NavigationSections.REVIEW_AND_PAY,
        },
      },
      [LongFormStates.LeaseAgreement]: {
        initial: 'callingGenerateContract',
        entry: AssignInitialLeaseDocumentLanguage,
        states: {
          callingGenerateContract: {
            tags: 'callingGenerateContract',
            invoke: {
              src: (context, _) => generateContract(context.orderId),
              onDone: [
                {
                  cond: (_, event) => event.data?.generateContractErrors?.length,
                  target: 'waiting', // let the page handle it
                  actions: [
                    assign({ generateContractErrors: (_, event: any) => event.data?.generateContractErrors }),
                    track('Internal Error Occurred - Lease Agreement'),
                  ],
                },
                {
                  actions: AssignGeneratedLeaseDocuments,
                  target: 'waiting',
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
            on: {
              [LongFormEvents.LANGUAGE_CHANGED]: createLanguageChangedTransitions(),
            },
          },
          waiting: {
            on: {
              [LongFormEvents.NEXT]: 'callingSignLeaseAgreement',
              [LongFormEvents.REFRESH_SIGNING_STATUS]: 'refreshingSigningStatus',
              [LongFormEvents.LANGUAGE_CHANGED]: createLanguageChangedTransitions(),
            },
          },
          callingSignLeaseAgreement: {
            tags: 'calling',
            invoke: {
              src: (context) => signLeaseAgreement(context.application?.lease?.id),
              onDone: [
                {
                  cond: (_, event) => event.data?.signingErrors?.length,
                  target: 'waiting',
                  actions: [assign({ signingErrors: (_, event: any) => event.data?.signingErrors }), track('Signing Error')],
                },
                {
                  target: 'routeAfterSigning',
                  // OR-2774: tracked here rather than on the LeaseSigned page, because
                  // merchant and Protection Plus routes never render it (see routeAfterSigning).
                  actions: [assign({ leaseSigned: true }), track('Lease Agreement Sign Successful')],
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
          // this state is only reachable via the contractSummaryPage transition
          callGetExistingLeaseDocuments: {
            tags: 'calling',
            invoke: {
              src: (context, _) => getExistingLeaseDocuments(context.orderId),
              onDone: [
                {
                  cond: (_, event) => event.data?.getExistingLeaseDocumentsErrors?.length,
                  target: 'waiting', // let the page handle it
                  actions: [
                    assign({ getExistingLeaseDocumentsErrors: (_, event: any) => event.data?.getExistingLeaseDocumentsErrors }),
                    track('Internal Error Occurred - Getting Lease Agreement'),
                  ],
                },
                {
                  actions: assign({ leaseDocuments: (_, event: any) => event.data }),
                  target: 'waiting',
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
          routeToProtectionPlus: {
            tags: 'calling',
            invoke: {
              src: (context) => getProtectionPlusEnrollmentUrl(context.application?.lease?.id!),
              onDone: [
                {
                  cond: (_, event) => event.data?.errors?.length > 0,
                  target: '#LongForm.LeaseComplete', //should there be an error screen saying error occured getting P+ enrollment url?
                  actions: assign({ protectionPlusEnrollmentErrors: (_, event) => event.data.errors }),
                },
                {
                  actions: (_, event) => {
                    const url = new URL(event.data.url);
                    url.searchParams.set(QueryParamKeys.Language, i18n.resolvedLanguage ?? 'en');
                    window.location.href = url.toString();
                  },
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
          refreshingSigningStatus: {
            tags: 'calling',
            invoke: {
              src: queryOrder,
              onDone: {
                target: 'checkSigningResult',
                actions: assign({
                  application: (_, event) => event.data?.application,
                  orderDetails: (_, event) => event.data?.details,
                }),
              },
              onError: {
                target: 'waiting',
                actions: PropagateNetworkErrors(),
              },
            },
          },
          checkSigningResult: {
            always: [
              {
                cond: 'IsDocsSigned',
                target: 'routeAfterSigning',
              },
              {
                target: 'waiting',
              },
            ],
          },
          routeAfterSigning: {
            always: [
              {
                cond: 'OfferProtectionPlus',
                target: 'routeToProtectionPlus',
              },
              {
                cond: (context) => merchantRoles.includes(context.role),
                target: 'redirectToAppDetails',
              },
              {
                target: '#LongForm.LeaseComplete',
              },
            ],
          },
          redirectToAppDetails: {
            type: 'final',
            entry: (context) => redirectToApplicationDetails(context.application?.lease?.publicId),
          },
        },
        on: {
          [LongFormEvents.BACK]: {
            target: LongFormStates.ContractSummaryEcomm,
          },
        },
        meta: { navigationSection: NavigationSections.REVIEW_AND_PAY }, // TODO: this maybe shouldnt be here
      },
      [LongFormStates.LeaseComplete]: {},
    },
    schema: {
      context: {} as LongFormMachineContext,
      events: {} as LongFormMachineEvents,
      guards: {} as LongFormMachineGuards,
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      ...sharedActions,
      assignAccommodationAndBillingAddresses: assign({
        // Initialize billing address too because it's currently required in AES
        accommodationAddress: (_context, event: any) => event.data,
        // use billing address if its correctly populated
        billingAddress: (context, event: any) => (isValidAddress(context.billingAddress) ? context.billingAddress : event.data),
      }),
    },
    guards: {
      NoApplication: (context) => {
        return !context.application?.lease?.id;
      },
      NoAppStep: (context) => {
        return !context.application?.lease?.appStep;
      },
      AppStepPersonalInformation: (context) => {
        return context.application?.lease?.appStep === AppSteps.PersonalInformation;
      },
      AppStepContactData: (context) => {
        return context.application?.lease?.appStep === AppSteps.ContactData;
      },
      AppStepIncomeSource: (context) => {
        return context.application?.lease?.appStep === AppSteps.IncomeSource;
      },
      AppStepBankingInformation: (context) => {
        return context.application?.lease?.appStep === AppSteps.BankingInformation;
      },
      // REMOVE WHEN FF IS REMOVED
      AppStepBankingInformationFF: (context) => {
        return context.application?.lease?.appStep === 'bankingInformation' && determinePlaidFF(context);
      },
      AppStepDebitCardInformation: (context) => {
        return context.application?.lease?.appStep === AppSteps.DebitCardInformation;
      },
      // OR-2278: used to be split into an M3+ guard (-> reviewAndSubmit) and a below-M3 guard
      // (-> the now-removed TransferToRapid). Both ends now point at reviewAndSubmit, so the
      // milestone no longer changes the outcome — collapsed into one guard.
      AppStepReviewAndSubmit: (context) => {
        return (
          context.application?.lease?.appStep === AppSteps.ReviewAndSubmit &&
          (context.application?.lease.status === 'draft' || context.application?.lease?.status === 'errorsFound')
        );
      },
      AppStepAmendAndResubmit: (context) => {
        return context.application?.lease?.appStep === AppSteps.AmendAndResubmit;
      },
      ViewApplicationDetails: (context) => {
        return context.application?.lease?.status !== 'draft' && context.application?.lease?.status !== 'errorsFound' && !!context.isReadOnly;
      },
      SkipToApprovalConfirmation: (context) => {
        return context.skipToPage === 'ApprovalConfirmation' && context.dealer.channelType !== ChannelTypes.Ecommerce;
      },
      SkipToDeclined: (context) => {
        return context.skipToPage === 'Declined';
      },
      SkipToReviewAndSubmit: (context) => {
        return context.skipToPage === 'ReviewAndSubmit';
      },
      SkipToCompleteAppLater: (context) => {
        return context.skipToPage === 'CompleteAppLater';
      },
      PreApproval: (context) => {
        return context.application?.lease?.status === 'preApproved';
      },
      PostApprovalBM: (context) => {
        return (
          (context.application?.lease?.status === 'approved' || context.application?.lease?.status === 'docsReady') &&
          context.dealer.channelType !== ChannelTypes.Ecommerce
        );
      },
      TransitionedFromReviewAndSubmit: (context) => {
        return context.transitionedFrom === LongFormStates.ReviewAndSubmit;
      },
      TransitionedFromContractSummaryEcomm: (context) => {
        return context.transitionedFrom === LongFormStates.ContractSummaryEcomm;
      },
      TransitionedFromAppSteps: (context) => {
        return context.transitionedFrom !== LongFormStates.ReviewAndSubmit;
      },
      CustomerOTB: (context, event) => {
        var applyType = event.data?.eligibilityDetails?.applyTypes[0];
        return applyType === ApplyTypes.LeaseOtb || applyType === ApplyTypes.LoanOtb;
      },
      IsIneligible: (_context, event) => {
        return event.data?.eligibilityDetails?.isEligible === false || event.data?.eligibilityDetails?.otbCode === 4;
      },
      IsIneligibleResolverError: (_context, event) =>
        !!event.data?.resolverErrors?.length && event.data?.resolverErrors.some((err: any) => err.message === 'customer is ineligible'),
      BankAccountHardDecline: (context, event) =>
        !!event?.data?.updateBankAccountErrors?.length &&
        event?.data?.updateBankAccountErrors.some((err: UpdateBankAccountErrors) => err.__typename === 'HardDecline'),
      // Payment Selection/ Plaid specific guards
      PaymentSelectFF: (context, event) => {
        return determinePlaidFF(context);
      },
      EnterPaymentSelectFF: (context, event) => {
        return (
          determinePlaidFF(context) &&
          (context?.application?.lease?.draftStep === 'bankingInformation' || context?.application?.lease?.draftStep === 'bankAccount')
        );
      },
      TokenizedAcct: (_context, event) => {
        if (
          event?.data?.updateBankAccountErrors?.length &&
          event?.data?.updateBankAccountErrors.some((err: UpdateBankAccountErrors) => err.__typename === 'BankConnectionError')
        ) {
          // TODO: put this condition in the some predicate
          // @ts-ignore
          return event.data.updateBankAccountErrors[0].extensions.errCode === 'tokenized';
        }
        return false;
      },
      BankError: (_, event) =>
        !!event.data?.updateBankAccountErrors?.some(
          (err: UpdateBankAccountErrors) => err.__typename === 'SoftDecline' && err?.extensions?.declineType === SoftDeclineTypes.BankError,
        ),
      PaymentSelectionError: (_context, event) => {
        // General error coming back, etc...
        return !!event?.data?.updateBankAccountErrors && event?.data?.updateBankAccountErrors.length > 0;
      },
      IneligibleBank: (_context, event) => {
        // General error coming back, etc...
        return !!(event?.data?.updateBankAccountErrors && event.data.updateBankAccountErrors.length > 0);
      },
      PlaidSuccess: (_context, event) => {
        return !!event?.data?.updateBankAccountErrors && event?.data?.updateBankAccountErrors.length === 0;
      },
      LinkTokenFailure: (_context, event) => {
        return !!event?.data?.generateBankConnectorTokenErrors && event?.data?.generateBankConnectorTokenErrors.length > 0;
      },
      IsHardDecline: (context) => !!context.hardDeclineError,
      IsSoftDecline: (context) => !!context.softDeclineError?.extensions?.decisionCode,
      ApplyIneligible: (context) => !!context.applyErrors?.some((err) => err.__typename === 'Ineligible'),
      ApplicationExists: (context, _event) => !!context.application,
      CustomerNotFound: (_context, event) => {
        event = event as LongFormMachineEvents;
        return event.data?.customerFlowType === CustomerFlowType.NewCustomer;
      },
      IsEcommPostIP: (context, _event) => {
        return (
          context.dealer.channelType === ChannelTypes.Ecommerce &&
          (context.application?.lease?.status === AppStatuses.Approved || context.application?.lease?.status === AppStatuses.DocsReady)
        );
      },
      IsEcommLeaseComplete: (context, _event) => {
        return context.dealer.channelType === ChannelTypes.Ecommerce && context.application?.lease?.status === AppStatuses.DocsSigned;
      },
      SkipToContractSummary: (context) => {
        return context.skipToPage === 'ContractSummary' && context.dealer.channelType === ChannelTypes.Ecommerce;
      },
      OfferProtectionPlus: (context) =>
        context.highestSupportedApplicationMilestone >= 4 &&
        context.dealer.channelType === ChannelTypes.BrickAndMortar &&
        !!context.dealer.isLTOInsurance &&
        !['UT', 'OK', 'IN', 'AK', 'RI'].includes(context.dealer.dealerAddress.state) &&
        context.application?.customer?.protectionPlusStatus != ProtectionPlusStatus.Enrolled &&
        context.application?.customer?.protectionPlusStatus != ProtectionPlusStatus.OptedIn,
      IsDocsSigned: (context) => context.application?.lease?.status === 'docsSigned',
      LTOIneligible: (context, event) => {
        if (event?.data?.createDraftAppErrors?.length) {
          return event?.data?.createDraftAppErrors.some((err) => err.__typename === 'LeaseRestricted');
        }
        if (context.applyErrors?.length) {
          return context.applyErrors.some((err) => err.__typename === 'LeaseRestricted');
        }
        return false;
      },
      LTOIneligibleAutoVertical: (context, event) => {
        if (event?.data?.createDraftAppErrors?.length) {
          return event?.data?.createDraftAppErrors?.some((err) => err.__typename === 'LeaseRestricted' && err.extensions?.reason === 'AutoVertical');
        }
        if (context.applyErrors?.length) {
          return context.applyErrors.some((err) => err.__typename === 'LeaseRestricted' && err.extensions?.reason === 'AutoVertical');
        }
        return false;
      },
    },
  },
);
